import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp, Trophy, TrendingUp, TrendingDown, Clock, Gavel, User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Badge } from "./ui/badge";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";

// Collapsible auction card for ADMIN view - shows all bids on a product
export function AdminAuctionCard({ productId, productTitle, productImage, bids, endTime }) {
  const [expanded, setExpanded] = useState(false);
  
  // Sort bids by amount descending
  const sortedBids = [...bids].sort((a, b) => b.amount - a.amount);
  const highestBid = sortedBids[0];
  const totalBids = sortedBids.length;
  const uniqueBidders = new Set(sortedBids.map(b => b.user_id || b.buyer_id)).size;
  
  // Check if auction is still active
  const isActive = endTime ? new Date(endTime) > new Date() : true;
  const timeLeft = endTime ? formatDistanceToNow(new Date(endTime), { locale: es }) : null;
  
  return (
    <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden hover:shadow-md transition-shadow">
      {/* Collapsed view - always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center gap-4 text-left"
        data-testid={`auction-card-${productId}`}
      >
        {/* Product image */}
        <div className="w-16 h-16 rounded-lg overflow-hidden bg-[#F1F5F9] flex-shrink-0">
          <img
            src={productImage}
            alt={productTitle}
            className="w-full h-full object-cover"
          />
        </div>
        
        {/* Product info */}
        <div className="flex-1 min-w-0">
          <Link 
            to={`/product/${productId}`} 
            className="font-semibold text-[#0F172A] hover:text-[#475569] line-clamp-1"
            onClick={(e) => e.stopPropagation()}
          >
            {productTitle}
          </Link>
          
          <div className="flex items-center gap-3 mt-1">
            {/* Highest bid */}
            <div className="flex items-center gap-1">
              <Trophy size={14} className="text-[#F59E0B]" />
              <span className="font-bold text-[#0F172A]">${highestBid?.amount?.toLocaleString()}</span>
            </div>
            
            {/* Number of bids */}
            <div className="flex items-center gap-1 text-sm text-[#64748B]">
              <Gavel size={14} />
              <span>{totalBids} ofertas</span>
            </div>
            
            {/* Unique bidders */}
            <div className="flex items-center gap-1 text-sm text-[#64748B]">
              <User size={14} />
              <span>{uniqueBidders}</span>
            </div>
          </div>
          
          {/* Time left */}
          {timeLeft && (
            <div className="flex items-center gap-1 mt-1 text-xs">
              <Clock size={12} className={isActive ? "text-[#F59E0B]" : "text-[#64748B]"} />
              <span className={isActive ? "text-[#F59E0B]" : "text-[#64748B]"}>
                {isActive ? `Termina en ${timeLeft}` : "Finalizada"}
              </span>
            </div>
          )}
        </div>
        
        {/* Highest bidder avatar */}
        <div className="flex items-center gap-2">
          <Avatar className="h-8 w-8">
            <AvatarImage src={highestBid?.buyer_picture} />
            <AvatarFallback className="bg-[#475569] text-white text-xs">
              {highestBid?.buyer_name?.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div className="text-right hidden sm:block">
            <p className="text-sm font-medium text-[#0F172A]">{highestBid?.buyer_name}</p>
            <p className="text-xs text-[#64748B]">Líder</p>
          </div>
        </div>
        
        {/* Expand icon */}
        <div className="flex-shrink-0 text-[#64748B]">
          {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </button>
      
      {/* Expanded view - bid history */}
      {expanded && (
        <div className="border-t border-[#E2E8F0] bg-[#F8FAFC] p-4">
          <h4 className="text-sm font-semibold text-[#64748B] mb-3">Historial de Ofertas</h4>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {sortedBids.map((bid, index) => (
              <div 
                key={bid.bid_id} 
                className={`flex items-center justify-between p-2 rounded-lg ${
                  index === 0 ? "bg-[#FEF3C7] border border-[#F59E0B]" : "bg-white border border-[#E2E8F0]"
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#64748B] text-white text-xs flex items-center justify-center">
                    {index + 1}
                  </span>
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={bid.buyer_picture} />
                    <AvatarFallback className="bg-[#475569] text-white text-xs">
                      {bid.buyer_name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-sm font-medium text-[#0F172A]">{bid.buyer_name}</p>
                    <p className="text-xs text-[#64748B]">
                      {formatDistanceToNow(new Date(bid.created_at), { addSuffix: true, locale: es })}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`font-bold ${index === 0 ? "text-[#F59E0B]" : "text-[#0F172A]"}`}>
                    ${bid.amount?.toLocaleString()}
                  </p>
                  {index === 0 && (
                    <Badge className="bg-[#F59E0B] text-white text-xs">Líder</Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Collapsible auction card for USER view - shows their bids on products
export function UserAuctionCard({ productId, productTitle, productImage, userBids, currentHighestBid, isWinning, endTime }) {
  const [expanded, setExpanded] = useState(false);
  
  // Sort user's bids by amount descending
  const sortedBids = [...userBids].sort((a, b) => b.amount - a.amount);
  const userHighestBid = sortedBids[0];
  
  // Check if auction is still active
  const isActive = endTime ? new Date(endTime) > new Date() : true;
  const timeLeft = endTime ? formatDistanceToNow(new Date(endTime), { locale: es }) : null;
  
  return (
    <div className={`bg-white rounded-xl border-2 overflow-hidden hover:shadow-md transition-shadow ${
      isWinning ? "border-green-400" : "border-red-300"
    }`}>
      {/* Collapsed view */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center gap-4 text-left"
        data-testid={`user-auction-${productId}`}
      >
        {/* Product image */}
        <div className="w-16 h-16 rounded-lg overflow-hidden bg-[#F1F5F9] flex-shrink-0 relative">
          <img
            src={productImage}
            alt={productTitle}
            className="w-full h-full object-cover"
          />
          {/* Status indicator */}
          <div className={`absolute -top-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center ${
            isWinning ? "bg-green-500" : "bg-red-500"
          }`}>
            {isWinning ? (
              <TrendingUp size={14} className="text-white" />
            ) : (
              <TrendingDown size={14} className="text-white" />
            )}
          </div>
        </div>
        
        {/* Product info */}
        <div className="flex-1 min-w-0">
          <Link 
            to={`/product/${productId}`} 
            className="font-semibold text-[#0F172A] hover:text-[#475569] line-clamp-1"
            onClick={(e) => e.stopPropagation()}
          >
            {productTitle}
          </Link>
          
          {/* Status badge */}
          <div className="mt-1">
            {isWinning ? (
              <Badge className="bg-green-100 text-green-700 border-green-300">
                <Trophy size={12} className="mr-1" /> ¡Vas ganando!
              </Badge>
            ) : (
              <Badge className="bg-red-100 text-red-700 border-red-300">
                <TrendingDown size={12} className="mr-1" /> Te superaron
              </Badge>
            )}
          </div>
          
          {/* Time left */}
          {timeLeft && isActive && (
            <div className="flex items-center gap-1 mt-1 text-xs">
              <Clock size={12} className="text-[#F59E0B]" />
              <span className="text-[#F59E0B]">Termina en {timeLeft}</span>
            </div>
          )}
        </div>
        
        {/* Bid amounts */}
        <div className="text-right">
          <div className="mb-1">
            <p className="text-xs text-[#64748B]">Tu oferta</p>
            <p className={`font-bold ${isWinning ? "text-green-600" : "text-[#0F172A]"}`}>
              ${userHighestBid?.amount?.toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-xs text-[#64748B]">Oferta más alta</p>
            <p className={`font-bold ${!isWinning ? "text-red-600" : "text-[#0F172A]"}`}>
              ${currentHighestBid?.toLocaleString()}
            </p>
          </div>
        </div>
        
        {/* Expand icon */}
        <div className="flex-shrink-0 text-[#64748B]">
          {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </button>
      
      {/* Expanded view - user's bid history */}
      {expanded && (
        <div className="border-t border-[#E2E8F0] bg-[#F8FAFC] p-4">
          <h4 className="text-sm font-semibold text-[#64748B] mb-3">Tu Historial de Ofertas</h4>
          <div className="space-y-2">
            {sortedBids.map((bid, index) => (
              <div 
                key={bid.bid_id} 
                className={`flex items-center justify-between p-2 rounded-lg ${
                  index === 0 ? "bg-white border-2 border-[#475569]" : "bg-white border border-[#E2E8F0]"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-[#64748B] text-white text-xs flex items-center justify-center">
                    {index + 1}
                  </span>
                  <p className="text-xs text-[#64748B]">
                    {formatDistanceToNow(new Date(bid.created_at), { addSuffix: true, locale: es })}
                  </p>
                </div>
                <div className="text-right">
                  <p className={`font-bold ${index === 0 ? "text-[#475569]" : "text-[#64748B]"}`}>
                    ${bid.amount?.toLocaleString()}
                  </p>
                  {index === 0 && (
                    <span className="text-xs text-[#64748B]">Tu mejor oferta</span>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {/* Action button */}
          {!isWinning && isActive && (
            <Link
              to={`/product/${productId}`}
              className="mt-3 w-full bg-[#475569] text-white py-2 rounded-full text-center block hover:bg-[#334155] transition-colors"
            >
              ¡Ofertar de nuevo!
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

export default { AdminAuctionCard, UserAuctionCard };
